/**
 * GeoPackage bytes kept in the browser's Cache API across reloads and visits, keyed by object,
 * path and listing digest: a changed object has a new digest, so a stale file is never served.
 * Only the most recently used files are kept. Any failure of the Cache API (none in an insecure
 * context, a full quota, a private window) is a miss, never an error.
 */

export const GPKG_CACHE_MAX_ENTRIES = 10;
// v2: the S-JTSK transform changed (crs.ts); copies reprojected under v1 are not to be served.
const CACHE_NAME = 'forestum-gpkg-v2';
const OLD_CACHE_NAMES = ['forestum-gpkg-v1'];
// A key only: nothing is ever fetched from this origin.
const KEY_ORIGIN = 'https://gpkg-cache.invalid';

export interface GpkgCache {
  get(object: string, path: string, digest: string): Promise<Uint8Array | null>;
  put(object: string, path: string, digest: string, bytes: Uint8Array): Promise<void>;
  remove(object: string, path: string, digest: string): Promise<void>;
}

function keyOf(object: string, path: string, digest: string): string {
  return `${KEY_ORIGIN}/${encodeURIComponent(object)}/${encodeURIComponent(digest)}/${encodeURIComponent(path)}`;
}

export function createGpkgCache(
  storage: () => CacheStorage | undefined = () => globalThis.caches,
  maxEntries: number = GPKG_CACHE_MAX_ENTRIES,
): GpkgCache {
  let pruned = false;
  const open = async (): Promise<Cache | null> => {
    const caches = storage();
    if (!caches) return null;
    if (!pruned) {
      pruned = true;
      // Files kept under an earlier name hold copies made under rules that no longer hold.
      await Promise.all(OLD_CACHE_NAMES.map((name) => caches.delete(name).catch(() => false)));
    }
    return caches.open(CACHE_NAME);
  };

  const store = async (cache: Cache, key: string, bytes: Uint8Array): Promise<void> => {
    await cache.delete(key);
    await cache.put(key, new Response(bytes.slice()));
  };

  return {
    async get(object, path, digest) {
      if (!digest) return null;
      try {
        const cache = await open();
        const key = keyOf(object, path, digest);
        const hit = await cache?.match(key);
        if (!cache || !hit) return null;
        const bytes = new Uint8Array(await hit.arrayBuffer());
        await store(cache, key, bytes);
        return bytes;
      } catch {
        return null;
      }
    },

    async put(object, path, digest, bytes) {
      if (!digest) return;
      try {
        const cache = await open();
        if (!cache) return;
        await store(cache, keyOf(object, path, digest), bytes);
        const keys = await cache.keys();
        await Promise.all(keys.slice(0, Math.max(0, keys.length - maxEntries)).map((k) => cache.delete(k)));
      } catch {
        // A cache that cannot store is the same as no cache.
      }
    },

    async remove(object, path, digest) {
      try {
        await (await open())?.delete(keyOf(object, path, digest));
      } catch {
        // Nothing to drop, or no cache at all.
      }
    },
  };
}

export const gpkgCache = createGpkgCache();
